import React, { useEffect, useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

type Status = "idle" | "submitting" | "success" | "error";

const FeedbackDrawer: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState<Status>("idle");
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    feedback: "",
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatus("submitting");
    try {
      const write = addDoc(collection(db, "feedback"), {
        ...form,
        submittedAt: serverTimestamp(),
      });
      const timeout = new Promise<never>((_, reject) =>
        setTimeout(
          () => reject(new Error("Feedback submit timed out")),
          15000
        )
      );
      await Promise.race([write, timeout]);
      setStatus("success");
      setForm({ firstName: "", lastName: "", email: "", feedback: "" });
    } catch {
      setStatus("error");
    }
  };

  const handleClose = () => {
    setOpen(false);
    if (status === "success") setStatus("idle");
  };

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  return (
    <>
      {/* Overlay */}
      {open && (
        <div className="feedback-overlay" onClick={handleClose} />
      )}

      {/* Pill trigger */}
      <button
        className={`feedback-pill${open ? " feedback-pill--open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Open feedback"
      >
        <span>Feedback</span>
      </button>

      {/* Drawer panel */}
      <div className={`feedback-drawer${open ? " feedback-drawer--open" : ""}`}>
        <div className="feedback-drawer__header">
          <h2>Share Your Feedback</h2>
          <button
            className="feedback-drawer__close"
            onClick={handleClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {status === "success" ? (
          <div className="feedback-success">
            <div className="feedback-success__icon">✓</div>
            <h3>Thank you!</h3>
            <p>Your feedback has been submitted. We appreciate you taking the time to share your thoughts.</p>
            <button
              className="feedback-btn feedback-btn--secondary"
              onClick={handleClose}
            >
              Close
            </button>
          </div>
        ) : (
          <form className="feedback-form" onSubmit={handleSubmit}>
            <div className="feedback-form__row">
              <div className="feedback-form__field">
                <label htmlFor="fb-firstName">First Name</label>
                <input
                  id="fb-firstName"
                  name="firstName"
                  type="text"
                  value={form.firstName}
                  onChange={handleChange}
                  required
                  placeholder="Jane"
                />
              </div>
              <div className="feedback-form__field">
                <label htmlFor="fb-lastName">Last Name</label>
                <input
                  id="fb-lastName"
                  name="lastName"
                  type="text"
                  value={form.lastName}
                  onChange={handleChange}
                  required
                  placeholder="Smith"
                />
              </div>
            </div>

            <div className="feedback-form__field">
              <label htmlFor="fb-email">Email Address</label>
              <input
                id="fb-email"
                name="email"
                type="email"
                value={form.email}
                onChange={handleChange}
                required
                placeholder="jane@example.com"
              />
            </div>

            <div className="feedback-form__field">
              <label htmlFor="fb-feedback">Your Feedback</label>
              <textarea
                id="fb-feedback"
                name="feedback"
                value={form.feedback}
                onChange={handleChange}
                required
                rows={6}
                placeholder="Tell us what you think about Pahincho — what's working well, what could be better, or any ideas you'd like to share..."
              />
            </div>

            {status === "error" && (
              <p className="feedback-error">
                Something went wrong. Please try again.
              </p>
            )}

            <button
              type="submit"
              className="feedback-btn"
              disabled={status === "submitting"}
            >
              {status === "submitting" ? "Submitting…" : "Submit Feedback"}
            </button>
          </form>
        )}
      </div>
    </>
  );
};

export default FeedbackDrawer;
